/**
 * Platform route group.
 *
 * Platform health, metering and billing, AI reports, global search, the admin
 * dashboard placeholder, transactions, insurance admin, migration console,
 * settings, unauthorized and the catch-all route.
 *
 * Extracted verbatim from AppRoutes.tsx. The declaration sequence is preserved
 * exactly as part of the route structural contract; see
 * `__tests__/AppRoutes.structure.test.tsx`.
 */

import { lazy } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext.js';
import { ProtectedRoute } from '../../auth/components/ProtectedRoute';
import { ROUTES, permissionsFor } from '../routeCatalogue';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

const PlatformSystemHealth = lazy(() => import('../../pages/platform/SystemHealth'));
const PlatformServiceDetail = lazy(() => import('../../pages/platform/ServiceDetail'));
const PlatformInfrastructureHealth = lazy(() => import('../../pages/platform/InfrastructureHealth'));
const PlatformMeteringDashboard = lazy(() => import('../../pages/platform/MeteringDashboard'));
const PlatformUsageDetail = lazy(() => import('../../pages/platform/UsageDetail'));
const PlatformBillingPlans = lazy(() => import('../../pages/platform/BillingPlans'));
const PlatformInvoiceManagement = lazy(() => import('../../pages/platform/InvoiceManagement'));
const PlatformMigrationConsole = lazy(() => import('../../pages/platform/MigrationConsole'));
const AIReports = lazy(() => import('../../pages/ai/AIReports'));
const AIReportDetail = lazy(() => import('../../pages/ai/AIReportDetail'));
const AIDataExplorer = lazy(() => import('../../pages/ai/AIDataExplorer'));
const SearchResults = lazy(() => import('../../features/global-search/pages/SearchResults'));
const Settings = lazy(() => import('../../components/Settings/Settings'));
const Unauthorized = lazy(() => import('../../components/Unauthorized'));
const TransactionList = lazy(() => import('../../pages/transactions/TransactionList'));
const TransactionDetail = lazy(() => import('../../pages/transactions/TransactionDetail'));
const AdminInsuranceProducts = lazy(() => import('../../pages/admin/insurance/InsuranceProducts'));
const AdminInsuranceQuotes = lazy(() => import('../../pages/admin/insurance/InsuranceQuotes'));
const AdminInsuranceUnderwriting = lazy(() => import('../../pages/admin/insurance/InsuranceUnderwriting'));
const AdminInsuranceClaims = lazy(() => import('../../pages/admin/insurance/InsuranceClaims'));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AdminDashboard = () => {
    const { theme } = useTheme();
    return <div style={{ color: theme === 'dark' ? '#fff' : '#000' }}>Admin Dashboard</div>;
};

// ---------------------------------------------------------------------------
// platformRoutes()
// ---------------------------------------------------------------------------

export function platformRoutes() {
    return (
        <>
        {/* Platform Management – Phase 9E.1 System Health */}
        <Route path={ROUTES.PLATFORM_HEALTH.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_HEALTH)}>
                <PlatformSystemHealth />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PLATFORM_HEALTH_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_HEALTH_DETAIL)}>
                <PlatformServiceDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PLATFORM_INFRASTRUCTURE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_INFRASTRUCTURE)}>
                <PlatformInfrastructureHealth />
            </ProtectedRoute>
        } />

        {/* Platform Management – Phase 9E.2 Metering & Billing */}
        <Route path={ROUTES.PLATFORM_METERING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_METERING)}>
                <PlatformMeteringDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PLATFORM_METERING_USAGE_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_METERING_USAGE_DETAIL)}>
                <PlatformUsageDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PLATFORM_BILLING_PLANS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_BILLING_PLANS)}>
                <PlatformBillingPlans />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PLATFORM_BILLING_INVOICES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_BILLING_INVOICES)}>
                <PlatformInvoiceManagement />
            </ProtectedRoute>
        } />

        {/* AI Reports & Data Explorer – Phase 9D.3 / 9D.4 */}
        <Route path={ROUTES.AI_REPORTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AI_REPORTS)}>
                <AIReports />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.AI_REPORTS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AI_REPORTS_DETAIL)}>
                <AIReportDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.AI_EXPLORER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AI_EXPLORER)}>
                <AIDataExplorer />
            </ProtectedRoute>
        } />

        {/* Global Search - requires customers:view */}
        <Route path={ROUTES.SEARCH.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.SEARCH)}>
                <SearchResults />
            </ProtectedRoute>
        } />

        {/* Admin Dashboard - placeholder */}
        <Route path={ROUTES.ADMIN.path} element={<AdminDashboard />} />

        {/* Transactions (Phase 10) */}
        <Route path={ROUTES.TRANSACTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.TRANSACTIONS)}>
                <TransactionList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.TRANSACTIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.TRANSACTIONS_DETAIL)}>
                <TransactionDetail />
            </ProtectedRoute>
        } />

        {/* Insurance Admin (Phase 9C.18) */}
        <Route path={ROUTES.ADMIN_INSURANCE_PRODUCTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSURANCE_PRODUCTS)}>
                <AdminInsuranceProducts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_INSURANCE_QUOTES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSURANCE_QUOTES)}>
                <AdminInsuranceQuotes />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_INSURANCE_UNDERWRITING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSURANCE_UNDERWRITING)}>
                <AdminInsuranceUnderwriting />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_INSURANCE_CLAIMS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSURANCE_CLAIMS)}>
                <AdminInsuranceClaims />
            </ProtectedRoute>
        } />

        {/* Migration Console (Phase 9E.8) */}
        <Route path={ROUTES.PLATFORM_MIGRATION.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PLATFORM_MIGRATION)}>
                <PlatformMigrationConsole />
            </ProtectedRoute>
        } />

        {/* Settings Route - requires settings:view */}
        <Route path={ROUTES.SETTINGS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.SETTINGS)}>
                <Settings />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.UNAUTHORIZED.path} element={<Unauthorized />} />
        <Route path={ROUTES.CATCH_ALL.path} element={<Navigate to="/dashboard" replace />} />
        </>
    );
}
